import { useCallback, useEffect, useState } from 'react';

import { useWizardState } from '~/components/WizardState/hooks/useWizardState/useWizardState';

export type CallbackResult = { success: boolean; error: any };
type FormControlCallback<T> = (state: T) => Promise<CallbackResult>;

enum Action {
  SUBMIT = 'SUBMIT',
  NEXT = 'NEXT',
}

export const useWizardFormControls = <T>(callback?: FormControlCallback<T>) => {
  const [action, setAction] = useState<string | undefined>(undefined);
  const { state, loading, setLoading, onClose, onError, triggerFormSubmission, nextStep, previousStep, reset } =
    useWizardState<T>();

  const innerSubmit = useCallback(async () => {
    // NOOP if no callback was provided
    if (!callback) {
      return;
    }
    setLoading(true);
    const { success, error } = await callback(state).catch((error) => ({ success: false, error }));
    setLoading(false);
    if (success) {
      return onClose();
    }
    onError(error);
  }, [callback, onClose, onError, setLoading, state]);

  useEffect(() => {
    const performAction = async () => {
      if (!action) {
        return;
      }
      // prevent useEffect triggering multiple times for the same action
      setAction(undefined);
      switch (action) {
        case Action.SUBMIT:
          return await innerSubmit();
        case Action.NEXT:
          return nextStep();
      }
    };
    performAction();
  }, [action, innerSubmit, nextStep, previousStep]);

  const submit = useCallback(async () => {
    if (await triggerFormSubmission()) {
      setAction(Action.SUBMIT);
    }
  }, [triggerFormSubmission]);

  const cancel = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const skip = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const next = useCallback(async () => {
    if (await triggerFormSubmission()) {
      setAction(Action.NEXT);
    }
  }, [triggerFormSubmission]);

  const back = useCallback(async () => {
    previousStep();
  }, [previousStep]);

  return {
    submit,
    skip,
    next,
    back,
    cancel,
    // allow disabling of buttons if component is loading
    disabled: loading,
  };
};
